import { useEffect, useState } from "react";
// react router
import { useNavigate } from "react-router";
// react redux
import { useSelector } from "react-redux";
// react hook form  and validation
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { addNonDigitalProductSchema } from "../../schemes/product/addNonDigitalProductSchema";
import { Controller } from "react-hook-form";
// axios
import EnhancedAxios from "../../axios/EnhancedAxios";
// mui
import {
    Stack,
    Typography,
    Switch,
    Box,
    FormControlLabel,
} from "@mui/material";
// components
import {
    PageTitle,
    ImagePreview,
    SubmitButton,
    InputField,
    SelectUnit,
    SelectCategory,
    ErrorMessage,
} from "../../components";
// alerts
import { errorAlert, successAlert } from "../../utils/alerts/alerts";
// types
import { ReduxUserReducerState } from "../../types/global";
import { AxiosResponse, AxiosError } from "axios";
// form values types
type FormValues = {
    img: File | null;
    name: string;
    price: number | undefined;
    final_price: number | undefined;
    quantity: number;
    ignore_quantity: boolean;
    description: string;
    categories: any | any[];
    unit: any;
    is_digital: boolean;
    is_disabled: boolean;
};
// page function
const AddNonDigitalProduct = () => {
    const navigate = useNavigate();
    const userToken = useSelector(
        (state: ReduxUserReducerState) => state.user.userToken
    );
    const [submitting, setSubmitting] = useState(false);
    // --------------------------- form handle ---------------------------
    const form = useForm<FormValues>({
        defaultValues: {
            img: null,
            name: "",
            price: 0,
            final_price: 0,
            quantity: 0,
            ignore_quantity: false,
            description: "",
            categories: [],
            unit: "",
            is_digital: false,
            is_disabled: false,
        },
        resolver: yupResolver(addNonDigitalProductSchema),
        mode: "all",
    });
    const {
        register,
        setValue,
        getValues,
        formState: { errors },
        handleSubmit,
        control,
        watch,
    } = form;
    useEffect(() => {
        setValue("quantity", watch("ignore_quantity") ? 0 : watch("quantity"));
    }, [watch("ignore_quantity")]);
    // submit form
    const onSubmit: SubmitHandler<FormValues> = (data: any) => {
        const newCats: any = [];
        data.categories.forEach((cat: any) => {
            newCats.push(cat.id);
        });
        const newData = { ...data, categories: newCats };
        const formData = new FormData();
        if (watch("ignore_quantity")) {
            data["quantity"] = 0;
        }
        for (let i in newData) {
            if (i == "categories") {
                newData[i].forEach((cat: any, index: number) => {
                    formData.append(`categories`, cat);
                });
            } else {
                formData.append(i, newData[i]);
            }
        }
        setSubmitting(true);
        EnhancedAxios("multipart/form-data", userToken)
            .post(`/admin/items`, formData)
            .then((res: AxiosResponse) => {
                successAlert(`تم اضافة منتج باسم ${data.name}`);
                setSubmitting(false);
                navigate(-1);
            })
            .catch((error: AxiosError) => {
                errorAlert(error);
                setSubmitting(false);
            });
    };
    return (
        <Stack gap="20px">
            <PageTitle title="اضافة منتج ملموس" />
            <Stack alignItems="center">
                <Stack
                    width="100%"
                    maxWidth="800px"
                    gap="10px"
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <ImagePreview
                        setValue={setValue}
                        errorMessage={errors?.img?.message}
                        IPObjectFit="contain"
                    />
                    <InputField
                        type="text"
                        label="اسم المنتج"
                        registration={{ ...register("name") }}
                        errorMessage={errors?.name?.message}
                    />
                    <InputField
                        type="number"
                        label="سعر المنتج"
                        registration={{ ...register("price") }}
                        errorMessage={errors?.price?.message}
                    />
                    <InputField
                        type="number"
                        label="السعر بعد الخصم"
                        registration={{ ...register("final_price") }}
                        errorMessage={errors?.final_price?.message}
                    />
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap="10px"
                    >
                        <Box flex="1">
                            <InputField
                                type="number"
                                label="الكمية"
                                registration={{ ...register("quantity") }}
                                errorMessage={errors?.quantity?.message}
                                disabled={
                                    watch("ignore_quantity") ? true : false
                                }
                                styles={{ width: "100%" }}
                            />
                        </Box>
                        <Controller
                            name="ignore_quantity"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch {...field} />}
                                    label="تجاهل الكمية"
                                />
                            )}
                        />
                    </Box>
                    <SelectUnit control={control} />
                    {errors?.unit ? (
                        <ErrorMessage errMessage={errors?.unit?.message} />
                    ) : (
                        <></>
                    )}
                    <SelectCategory
                        control={control}
                        watch={watch}
                        setValue={setValue}
                    />
                    {errors?.categories ? (
                        <ErrorMessage
                            errMessage={errors?.categories?.message}
                        />
                    ) : (
                        <></>
                    )}
                    <InputField
                        type="text"
                        label="وصف المنتج"
                        registration={{ ...register("description") }}
                        errorMessage={errors?.description?.message}
                        multiline={true}
                        minRows={3}
                        maxRows={6}
                    />
                    <Controller
                        name="is_disabled"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        {...field}
                                        checked={!watch("is_disabled")}
                                        onChange={(e) =>
                                            setValue(
                                                "is_disabled",
                                                !e.target.checked
                                            )
                                        }
                                    />
                                }
                                label="مفعل"
                            />
                        )}
                    />
                    <SubmitButton label="انشاء" disabled={submitting} />
                </Stack>
            </Stack>
        </Stack>
    );
};

export default AddNonDigitalProduct;
